import React, { useCallback, useEffect, useMemo, useState } from "react";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { getBimTree } from "../../../api/bim";
import { useParams } from "react-router-dom";
import { ChevronRight, ChevronDown, Folder, FileText } from "lucide-react";

// 스타일링을 위한 래퍼 (FileTreePanel과 유사한 스타일)
// 아이콘 커스터마이징
const renderIcon = (node) => {
  if (node.isLeaf) {
    return <FileText size={16} className="text-gray-400 mr-1 inline-block align-middle" />;
  }
  return <Folder size={16} className="text-yellow-500 fill-yellow-500 mr-1 inline-block align-middle" />;
};

export default function BimFileTree({
  selectionMode = "single",
  defaultExpanded = true,
  refreshKey,
  onPick,           // 파일 선택 (보기 페이지)
  onFolderSelect,   // 폴더 선택 (업로드 페이지)
}) {
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const { id: projectId } = useParams();

  const toRcNode = useCallback((item, type) => {
    if (type === "folder") {
      const childrenFolders = (item.children || []).map((child) => toRcNode(child, "folder"));
      const childrenFiles = (item.files || []).map((file) => toRcNode(file, "file"));
      
      return {
        key: `folder-${item.id}`,
        title: item.name,
        isLeaf: false,
        children: [...childrenFolders, ...childrenFiles],
        rawdata: item,     // 원본 데이터
        datatype: "folder" 
      };
    } else {
      // type === "file"
      return {
        key: `file-${item.id}`,
        title: item.name,
        isLeaf: true,
        rawdata: item,
        datatype: "file",
        // BIM API응답의 gltf_file 필드를 file url로 사용
        fileUrl: item.gltf_file 
      };
    }
  }, []);

  const loadTree = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getBimTree(projectId);
      // res.data = { folders: [], files: [] }
      const { folders = [], files = [] } = res.data || {};

      const folderNodes = folders.map((f) => toRcNode(f, "folder"));
      const fileNodes = files.map((f) => toRcNode(f, "file"));
      const rootNodes = [...folderNodes, ...fileNodes];

      setTreeData(rootNodes);
      
      if (defaultExpanded) {
        // 모든 폴더 펼치기? 아니면 1단계만?
        // 일단 루트 레벨 키만
        setExpandedKeys(rootNodes.map(n => n.key));
      }

    } catch (e) {
      console.error("BIM Tree Load Fail", e);
    } finally {
      setLoading(false);
    }
  }, [projectId, toRcNode, defaultExpanded]);

  useEffect(() => {
    loadTree();
  }, [loadTree, refreshKey]);

  // 검색 필터링
  const filteredTreeData = useMemo(() => {
    if (!q.trim()) return treeData;
    const term = q.toLowerCase();

    const filterNode = (node) => {
      // 자식 필터링
      const children = (node.children || []).map(filterNode).filter(Boolean);
      
      // 자신 매치 여부
      const selfMatch = node.title.toLowerCase().includes(term);

      if (selfMatch || children.length > 0) {
        return { ...node, children };
      }
      return null;
    };

    return treeData.map(filterNode).filter(Boolean);
  }, [q, treeData]);

  const handleSelect = (keys, info) => {
    const node = info.node;
    setSelectedKeys(keys);

    if (node.datatype === "folder") {
      onFolderSelect?.(node.rawdata);
    } else if (node.datatype === "file") {
      // 4D 뷰어용 format: { gltfUrl: ..., name: ... }
      const fileData = {
          ...node.rawdata,
          file: node.fileUrl // view 페이지나 upload 페이지에서 편하게 쓰도록
      };
      onPick?.(fileData);
    }
  };

  return (
    <div className="p-4 border rounded-xl bg-white h-full flex flex-col min-h-[500px]">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-slate-700 flex items-center gap-2">
            📁 4D 모델 탐색기
        </span>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="폴더/파일명 검색"
        className="mb-3 border rounded-lg px-2 py-1.5 text-sm w-full outline-none focus:border-blue-500 transition"
      />

      <div className="flex-1 overflow-auto">
        {loading ? (
          <p className="text-sm text-slate-500 text-center py-4">로딩 중...</p>
        ) : (
          <Tree
            treeData={filteredTreeData}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
            selectedKeys={selectedKeys}
            onSelect={handleSelect}
            expandAction="click"
            icon={renderIcon}
            showIcon={true}
            switcherIcon={(node) => {
                if(node.isLeaf) return null;
                return node.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />;
            }}
            className="text-sm"
          />
        )}
        {!loading && filteredTreeData.length === 0 && (
            <p className="text-xs text-slate-400 text-center mt-10">데이터가 없습니다.</p>
        )}
      </div>
    </div>
  );
}
